import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { useParams } from 'react-router-dom';
import classes from './style.module.scss';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import Loader from '~/app/common/Loader';
import useWebSocket from '~/app/hooks/useWebSocket';
import { useStoreState, useStoreActions } from '~/store';
import MentionsTextArea from '~/app/modules/form/MentionsTextArea';
import File from '~/app/modules/form/File';
import { toast } from 'react-toastify';
import {
    getChatStoreKey,
    MessageType,
    selectChatData,
    EMPTY_COMMUNICATION_CHAT_DATA,
} from '~/app/data/statuses/chat';
import { Machine, Message, OpenCommunicationChatPayload, Project } from '~/types';
import {
    CHAT_MAX_ATTACHMENTS,
    uploadChatImages,
} from '~/messages/uploadChatImages';
import { buildAcceptFromExtensions } from '~/messages/attachmentUtils';

export interface FormComponentProps {
    type: MessageType;
}

export type FormComponent = React.FC<FormComponentProps>;

const Form: FormComponent = ({ type }) => {

  const { id: routeMachineId, projectId: routeProjectId } = useParams<{
    id: Machine['id']
    projectId: Project['id']
  }>();
  const chatSession = useStoreState(state => state.communicationChat?.data ?? EMPTY_COMMUNICATION_CHAT_DATA);
  const basePath = getChatStoreKey(type);
  const machineId = useStoreState(state =>
    type === MessageType.MACHINE ? (state.communicationChat?.data?.machineId ?? '') : ''
  );
  const projectId = useStoreState(state =>
    type === MessageType.MACHINE ? (state.communicationChat?.data?.projectId ?? '') : ''
  );
  const {t} = useTranslation("issue.messages");
  const [message, setMessage] = useState('');
  const [draftFiles, setDraftFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileInputKey, setFileInputKey] = useState(0);
  const fileInputRef = useRef<any>(null);
  const {sendMessage} = useWebSocket();
  const issueId = useStoreState(state => String(selectChatData(state, type).id ?? ''));
  const chatUsers = useStoreState(state => selectChatData(state, type).chatUsers ?? []);
  const user = useStoreState(state => state.user.data);
  const allowedFileExtensions = useStoreState(state => state.machines.allowedChatFileExtensions);
  const chatAccept = useMemo(
    () => buildAcceptFromExtensions(allowedFileExtensions),
    [allowedFileExtensions],
  );
  const addChatUserAction = useStoreActions(actions => actions[basePath].addChatUser);
  const chatUsersFetchAction = useStoreActions(actions => actions[basePath].fetchChatUsers);
  const startMachineChatAction = useStoreActions(actions => actions.communicationChat.startMachineChat);
  const addChatMessageAction = useStoreActions(
    actions => type === MessageType.MACHINE
      ? actions.communicationChat.addChatMessage
      : actions[basePath].addChatMessage
  );

  const existingUserNames = useMemo(() => new Set(chatUsers.map(u => u.name)), [chatUsers]);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const parseMessage = useCallback((message: string) => {
    const regex = /@\[(.+?)\]\((.+?)\)/g;
    const userIds: number[] = [];
    const userNames: string[] = [];
    const replacedMessage = message.replace(regex, (match, display: string, id: number) => {
      userIds.push(id);
      userNames.push(display);
      return `<span style="color: var(--color-light-blue)">${display}</span>`;
    });
  
    return { userIds, userNames, replacedMessage };
  }, []);
  
  
  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const el = event.target;
    const newValue = el.value;
    const cursor = el.selectionStart;

    setMessage(newValue);

    requestAnimationFrame(() => {
      if (!isMountedRef.current) {
        return;
      }
      el.selectionStart = cursor;
      el.selectionEnd = cursor;
    });
  }, []);

  const handleFilesChange = useCallback((event: any) => {
    setDraftFiles(Array.isArray(event.target.value) ? event.target.value : []);
  }, []);

  const clearComposer = useCallback(() => {
    setMessage('');
    setDraftFiles([]);
    setUploadProgress(0);
    setUploading(false);
    setSending(false);
    setFileInputKey(key => key + 1);
  }, []);

  const checkAtLeastOneUser = useCallback(() => {
    return chatUsers.filter(cU => Number(cU.id) !== Number(user.id)).length === 0;
  }, [chatUsers, user]);

  const buildOutgoingMessage = useCallback((
    chatId: string | number,
    text: string,
    attachmentUrls?: string[],
  ): Message => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return {
      user: String(user.id),
      senderId: String(user.id),
      senderName: user.name,
      issueId: String(chatId),
      message: text,
      type,
      time: `${hours}:${minutes}`,
      dateTime: now.toISOString(),
      machineId: machineId ? String(machineId) : undefined,
      projectId: projectId ? String(projectId) : undefined,
      ...(attachmentUrls && attachmentUrls.length > 0 ? { attachmentUrls } : {}),
    };
  }, [user.id, user.name, type, machineId, projectId]);

  const mapUploadError = useCallback((errText: string) => {
    if (errText === 'maxFiles') {
      return t('uploadMaxFiles', { max: CHAT_MAX_ATTACHMENTS });
    }
    if (errText === 'maxSize') {
      return t('uploadMaxSize');
    }
    if (errText === 'invalidType') {
      return t('uploadInvalidType');
    }
    if (errText === 'uploadFailed' || errText.startsWith('apiResponseMessages')) {
      return t('uploadFailed');
    }
    return errText || t('uploadFailed');
  }, [t]);

  const {
    roleId: sessionRoleId,
    recipientUserIds: sessionUserIds,
    recipients: sessionRecipients,
  } = chatSession;

  const machineChatSession = useMemo((): OpenCommunicationChatPayload => ({
    machineId: machineId || routeMachineId,
    projectId: projectId || routeProjectId,
    roleId: sessionRoleId ?? 0,
    userIds: sessionUserIds ?? [],
    recipients: sessionRecipients,
  }), [
    machineId,
    routeMachineId,
    projectId,
    routeProjectId,
    sessionRoleId,
    sessionUserIds,
    sessionRecipients,
  ]);

  const hasMachineRecipients = useCallback(() => {
    const ids = machineChatSession.userIds ?? [];
    return ids.filter(id => Number(id) > 0 && Number(id) !== Number(user.id)).length > 0;
  }, [machineChatSession.userIds, user.id]);

  const hasOtherChatParticipant = useCallback(() => {
    if (hasMachineRecipients()) {
      return true;
    }
    return !checkAtLeastOneUser();
  }, [hasMachineRecipients, checkAtLeastOneUser]);

  const canSubmit = (message.trim().length > 0 || draftFiles.length > 0) && !sending;
  const canAttach = !sending && draftFiles.length < CHAT_MAX_ATTACHMENTS;

  const openCamera = useCallback(() => {
    if (!canAttach) {
      return;
    }
    fileInputRef.current?.openCamera();
  }, [canAttach]);

  const openAttachment = useCallback(() => {
    if (!canAttach) {
      return;
    }
    fileInputRef.current?.openDocument();
  }, [canAttach]);

  const send = useCallback(async () => {
    if (sending) {
      return;
    }
    try {
      const { userIds, userNames, replacedMessage } = parseMessage(message);
      const isMachineChat = type === MessageType.MACHINE;
      const isChatStarted = !!issueId && Number(issueId) > 0;
      const filesToUpload = draftFiles;

      if (!replacedMessage.trim() && filesToUpload.length === 0) {
        return;
      }

      if (isMachineChat) {
        if (!hasOtherChatParticipant()) {
          toast.error(t('pleaseAddAtLeastOneUser'));
          return;
        }
      } else if (userIds.length === 0 && checkAtLeastOneUser()) {
        toast.error(t('pleaseAddAtLeastOneUser'));
        return;
      }

      setSending(true);

      const userIdNum = Number(user.id);
      if (!chatUsers.some(({ id }) => Number(id) === userIdNum)) {
        userIds.push(userIdNum);
      }

      let activeChatId: string | number = issueId;
      if (isMachineChat && !isChatStarted) {
        activeChatId = await startMachineChatAction(machineChatSession);
        if (!isMountedRef.current) return;
      }

      const chatIdStr = String(activeChatId);
      if (!activeChatId || Number(activeChatId) === 0) {
        toast.error(isMachineChat ? t('couldNotStartChat') : t('pleaseAddAtLeastOneUser'));
        return;
      }

      if (userIds.length > 0) {
        await addChatUserAction({ issueId: Number(activeChatId), userIds });
        if (!isMountedRef.current) return;
        if (Number(activeChatId) > 0) {
          chatUsersFetchAction(String(activeChatId));
        }
      }
      if (!isMountedRef.current) return;

      const machineCtx = isMachineChat
        ? { machineId, projectId, roleId: machineChatSession.roleId }
        : {};

      let attachmentUrls: string[] = [];
      if (filesToUpload.length > 0) {
        setUploading(true);
        setUploadProgress(0);
        try {
          attachmentUrls = await uploadChatImages(
            filesToUpload,
            activeChatId,
            type,
            allowedFileExtensions ?? [],
            percent => {
              if (isMountedRef.current) {
                setUploadProgress(percent);
              }
            },
          );
        } catch (uploadError) {
          if (!isMountedRef.current) return;
          const errText = uploadError instanceof Error ? uploadError.message : String(uploadError);
          toast.error(mapUploadError(errText));
          return;
        } finally {
          if (isMountedRef.current) {
            setUploading(false);
          }
        }
        if (!isMountedRef.current) return;
      }

      if (!existingUserNames.has(user.name)) {
        sendMessage(t("joinChatUser", { name: user.name }), 'add', chatIdStr, type, machineCtx);
      }
      userNames.forEach((name) => {
        if (!existingUserNames.has(name)) {
          sendMessage(t("joinChatUser", { name }), 'add', chatIdStr, type, machineCtx);
        }
      });

      const sendCtx = {
        ...machineCtx,
        ...(attachmentUrls.length > 0 ? { attachmentUrls } : {}),
      };
      sendMessage(replacedMessage, '', chatIdStr, type, sendCtx);
      addChatMessageAction(buildOutgoingMessage(activeChatId, replacedMessage, attachmentUrls));

      clearComposer();
    } catch (error) {
      if (!isMountedRef.current) return;
      const errText = error instanceof Error ? error.message : String(error);
      if (errText) toast.error(mapUploadError(errText));
    } finally {
      if (isMountedRef.current) {
        setSending(false);
      }
    }
  }, [
    t, type, existingUserNames, user.id, user.name, issueId,
    chatUsersFetchAction, message, draftFiles, sending, sendMessage, addChatUserAction, parseMessage,
    checkAtLeastOneUser, hasOtherChatParticipant, machineId, projectId,
    startMachineChatAction, addChatMessageAction, buildOutgoingMessage,
    machineChatSession, clearComposer, mapUploadError,
    allowedFileExtensions, chatUsers,
  ]);

  return (
    <div className={classes['composer']}>
      <div className={classes['attachments']}>
        <File
          key={fileInputKey}
          ref={fileInputRef}
          className={classes['file-field']}
          name="chatImages"
          accept={chatAccept}
          maxFiles={CHAT_MAX_ATTACHMENTS}
          preview
          imageEdit
          showInput={false}
          disabled={sending}
          onChange={handleFilesChange}
        />
      </div>
      <div className={classes['form']}>
        <button
          type="button"
          className={classes['camera-btn']}
          onClick={openCamera}
          disabled={!canAttach}
          aria-label={t('camera')}
        >
          <Icon name="photo_camera" size="22" />
        </button>
        <div className={classes['input-shell']}>
          <MentionsTextArea
            embedded
            value={message}
            onChange={handleChange}
            placeholder={t('write')}
          />
          <button
            type="button"
            className={classes['attach-btn']}
            onClick={openAttachment}
            disabled={!canAttach}
            aria-label={t('addAttachment')}
          >
            <Icon name="attach_file" size="22" />
          </button>
        </div>
        <button
          type="button"
          className={classes['send-btn']}
          onClick={send}
          disabled={!canSubmit}
          aria-label={t('submit')}
          aria-busy={sending}
        >
          {sending ? (
            <Loader size="20" inverse inline />
          ) : (
            <Icon name="send" size="22" />
          )}
        </button>
      </div>
      {uploading && (
        <div className={classes['upload-progress']}>
          {t('uploading', { percent: uploadProgress })}
        </div>
      )}
    </div>
  );
};

export default Form
